import _regeneratorRuntime from "@babel/runtime/helpers/regeneratorRuntime";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { useEffect } from 'react';
import { useCentralizedProviderData, useMe } from '@crehana/auth';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';
import { useGetIntercomUserJwtQuery } from "./get-intercom-user-jwt.v5.generated";
export var LoadIntercom = function LoadIntercom(_ref) {
  var _config$enabled, _config$forceToLoad;
  var config = _ref.config;
  var isRenderedFromReactNativeWebView = useIsRenderedFromWebView();
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me,
    loading = _useCentralizedProvid.loading;
  var _useMe = useMe(),
    learningMe = _useMe.me,
    loadingLearningMe = _useMe.loading;
  var _useGetIntercomUserJw = useGetIntercomUserJwtQuery({
      context: {
        clientName: 'v5'
      }
    }),
    loadingJWT = _useGetIntercomUserJw.loading,
    dataJWT = _useGetIntercomUserJw.data;
  var intercomEnabled = (_config$enabled = config === null || config === void 0 ? void 0 : config.enabled) !== null && _config$enabled !== void 0 ? _config$enabled : true;
  var intercomForceToLoad = (_config$forceToLoad = config === null || config === void 0 ? void 0 : config.forceToLoad) !== null && _config$forceToLoad !== void 0 ? _config$forceToLoad : false;
  var isProduction = process.env.APP_ENV === 'production';
  var getAppId = function getAppId() {
    if (process.env.NODE_ENV === 'production') {
      return 'mevliu65';
    }

    // its a intercom messenger app for qa but we have decided to just load the production app for qa
    // to avoid miss-matching configurations or UI changes between the two apps
    // there were some issues where the qa app didn't have the same features as the production app
    // We'll keep this commented out for now but we can uncomment it if we need to
    // if (process.env.APP_ENV === 'qa') {
    //   return 'if08s3kq';
    // }

    return undefined;
  };
  var appId = getAppId();
  var _console = console,
    originalLog = _console.log;
  var log = function log() {
    var styles = {
      prefix: 'background: #6366f1; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
      text: 'color: #6366f1; font-weight: normal;'
    };
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    originalLog.apply(void 0, ['%c[Intercom]%c', styles.prefix, styles.text].concat(args));
  };
  useEffect(function () {
    var noAppId = !appId;
    var isLoading = loadingJWT || loadingLearningMe || loading;
    var isLogged = me || learningMe;
    // maybe there is a better way to check this?
    var insideB2BUrls = window.location.pathname.includes('/org/');
    if (noAppId || isLoading || !isLogged || !insideB2BUrls || isRenderedFromReactNativeWebView) {
      log('Intercom not loaded', {
        noAppId: noAppId,
        isLoading: isLoading,
        isLogged: isLogged,
        insideB2BUrls: insideB2BUrls,
        isRenderedFromReactNativeWebView: isRenderedFromReactNativeWebView
      });
      return;
    }
    var intercomCustomAttributes = {};
    if (me) {
      var _me$security$learning, _me$security$talentSe, _dataJWT$intercom, _dataJWT$intercom$get;
      intercomCustomAttributes.user_id = me.id.toString();
      intercomCustomAttributes.centralized_user_id = me.id.toString();
      intercomCustomAttributes.centralized_user_role = me.security.applicationRoles.find(function (role) {
        return role === 'ROLE_SUPERADMIN' || role === 'ROLE_ADMIN' || role === 'ROLE_USER';
      }) || 'ROLE_UNKNOWN';
      intercomCustomAttributes.name = me.user.fullName;
      intercomCustomAttributes.email = me.user.email;
      intercomCustomAttributes.created_at = me.user.createdAt;
      intercomCustomAttributes.company_name = me.organization.name;
      if ((_me$security$learning = me.security.learningSession) !== null && _me$security$learning !== void 0 && _me$security$learning.userId) {
        intercomCustomAttributes.centralized_learning_user_id = me.security.learningSession.userId;
      }
      if ((_me$security$talentSe = me.security.talentSession) !== null && _me$security$talentSe !== void 0 && _me$security$talentSe.userId) {
        intercomCustomAttributes.centralized_talent_user_id = me.security.talentSession.userId;
      }

      // The jwt token is only generated for centralized users
      if (dataJWT !== null && dataJWT !== void 0 && (_dataJWT$intercom = dataJWT.intercom) !== null && _dataJWT$intercom !== void 0 && (_dataJWT$intercom$get = _dataJWT$intercom.get_jwt_encode) !== null && _dataJWT$intercom$get !== void 0 && _dataJWT$intercom$get.jwt_encode) {
        intercomCustomAttributes.intercom_user_jwt = dataJWT.intercom.get_jwt_encode.jwt_encode;
      }
    } else if (learningMe) {
      var _learningMe$originalI, _learningMe$organizat, _learningMe$originalI2;
      intercomCustomAttributes.user_id = (_learningMe$originalI = learningMe.originalId) === null || _learningMe$originalI === void 0 ? void 0 : _learningMe$originalI.toString();
      intercomCustomAttributes.name = learningMe.fullName || '';
      intercomCustomAttributes.email = learningMe.email || '';
      intercomCustomAttributes.company_name = ((_learningMe$organizat = learningMe.organization) === null || _learningMe$organizat === void 0 ? void 0 : _learningMe$organizat.name) || '';
      intercomCustomAttributes.created_at = learningMe.dateJoined;
      intercomCustomAttributes.solo_learning_user_id = (_learningMe$originalI2 = learningMe.originalId) === null || _learningMe$originalI2 === void 0 ? void 0 : _learningMe$originalI2.toString();
      intercomCustomAttributes.solo_learning_user_role = learningMe.isB2BAdmin ? 'ROLE_ADMIN' : 'ROLE_USER';
    }
    log('Loading intercom...', {
      intercomForceToLoad: intercomForceToLoad,
      intercomCustomAttributes: intercomCustomAttributes,
      appId: appId
    });
    if (intercomEnabled && isProduction || intercomForceToLoad) {
      var loadAndInitIntercom = /*#__PURE__*/function () {
        var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
          var intercom;
          return _regeneratorRuntime().wrap(function _callee$(_context) {
            while (1) switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return import('@intercom/messenger-js-sdk').then(function (module) {
                  return module.Intercom;
                });
              case 2:
                intercom = _context.sent;
                intercom(_objectSpread({
                  app_id: appId,
                  debug: true
                }, intercomCustomAttributes));
              case 4:
              case "end":
                return _context.stop();
            }
          }, _callee);
        }));
        return function loadAndInitIntercom() {
          return _ref2.apply(this, arguments);
        };
      }();
      loadAndInitIntercom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- no required to depend on config because config is static
  }, [me, loading, loadingJWT, dataJWT, loadingLearningMe, learningMe]);
  return null;
};